import React, { forwardRef, useMemo } from 'react';
import PropTypes from 'prop-types';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import listPlugin from '@fullcalendar/list';
import interactionPlugin from '@fullcalendar/interaction';
import { LuChevronLeft, LuChevronRight } from 'react-icons/lu';
import { Card, Button, LoadingSpinner } from 'components/common';
import { withAlpha } from './calendarEvents';
import './bookingCalendar.css';

const PLUGINS = [dayGridPlugin, timeGridPlugin, interactionPlugin, listPlugin];
const EVENT_TIME_FORMAT = { hour: 'numeric', minute: '2-digit', omitZeroMinute: true, meridiem: 'short' };

// Time + title, laid out by bookingCalendar.css. List views show the time in
// their own column, so only the title there.
const renderEventContent = (arg) => {
  if (arg.view.type.startsWith('list')) return <span className="bc-event-title">{arg.event.title}</span>;

  return (
    <div className="bc-event" title={arg.event.title}>
      {arg.timeText && <span className="bc-event-time">{arg.timeText}</span>}
      <span className="bc-event-title">{arg.event.title}</span>
    </div>
  );
};

// Colour from the event's accent (calendarEvents#toCalendarEvent), as CSS vars.
const paintEvent = ({ el, event }) => {
  const accent = event.extendedProps?.accent;
  if (!accent) return;
  el.style.setProperty('--bc-accent', accent);
  el.style.setProperty('--bc-soft', withAlpha(accent, 0.12));
};

const Legend = ({ items }) => (
  <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-x-4 tw-gap-y-1 tw-px-4 tw-py-2 tw-border-0 tw-border-b tw-border-solid tw-border-slate-100 tw-bg-slate-50/60">
    {items.map(({ label, accent }) => (
      <span key={label} className="tw-inline-flex tw-items-center tw-gap-1.5 tw-text-xs tw-text-slate-600">
        <span className="tw-w-2.5 tw-h-2.5 tw-rounded-sm" style={{ background: accent }} aria-hidden="true" />
        {label}
      </span>
    ))}
  </div>
);

Legend.propTypes = {
  items: PropTypes.arrayOf(PropTypes.shape({ label: PropTypes.string, accent: PropTypes.string })).isRequired,
};

/**
 * The shared shell of the Car and Travel booking calendars: a toolbar (title +
 * navigation on the left, filters on the right), a colour legend, and a
 * FullCalendar styled by bookingCalendar.css.
 */
const BookingCalendarFrame = forwardRef(({
  title, filters, legend, events, loading, isRTL, onPrev, onNext, onToday, onEventClick,
}, ref) => {
  const plugins = useMemo(() => PLUGINS, []);

  return (
    <Card padding="none" className="tw-overflow-hidden">
      <div className="tw-flex tw-flex-wrap tw-items-center tw-justify-between tw-gap-3 tw-px-4 tw-py-3 tw-border-0 tw-border-b tw-border-solid tw-border-slate-200">
        <div className="tw-flex tw-items-center tw-gap-3">
          <Button size="sm" variant="secondary" onClick={onToday}>Today</Button>
          <div className="tw-inline-flex tw-items-center">
            <button type="button" onClick={onPrev} aria-label="Previous"
              className="tw-p-1.5 tw-rounded-md tw-border-0 tw-bg-transparent tw-text-slate-500 hover:tw-bg-slate-100 hover:tw-text-slate-800 tw-cursor-pointer">
              <LuChevronLeft size={18} />
            </button>
            <button type="button" onClick={onNext} aria-label="Next"
              className="tw-p-1.5 tw-rounded-md tw-border-0 tw-bg-transparent tw-text-slate-500 hover:tw-bg-slate-100 hover:tw-text-slate-800 tw-cursor-pointer">
              <LuChevronRight size={18} />
            </button>
          </div>
          <h4 className="tw-m-0 tw-text-lg tw-font-semibold tw-text-slate-800">{title}</h4>
          {loading && <LoadingSpinner size="sm" />}
        </div>
        <div className="tw-flex tw-items-center tw-gap-2">{filters}</div>
      </div>

      {legend?.length > 0 && <Legend items={legend} />}

      <div className="booking-calendar">
        <FullCalendar
          ref={ref}
          headerToolbar={false}
          plugins={plugins}
          initialView="dayGridMonth"
          themeSystem="standard"
          eventDisplay="block"
          dayMaxEvents={3}
          direction={isRTL ? 'rtl' : 'ltr'}
          height={800}
          stickyHeaderDates={false}
          nowIndicator
          eventTimeFormat={EVENT_TIME_FORMAT}
          eventContent={renderEventContent}
          eventDidMount={paintEvent}
          eventClick={onEventClick}
          events={events}
        />
      </div>
    </Card>
  );
});

BookingCalendarFrame.displayName = 'BookingCalendarFrame';

BookingCalendarFrame.propTypes = {
  title: PropTypes.string,
  filters: PropTypes.node,
  legend: PropTypes.array,
  events: PropTypes.array.isRequired,
  loading: PropTypes.bool,
  isRTL: PropTypes.bool,
  onPrev: PropTypes.func.isRequired,
  onNext: PropTypes.func.isRequired,
  onToday: PropTypes.func.isRequired,
  onEventClick: PropTypes.func,
};

export default BookingCalendarFrame;
